import { StatusType, StatusVisualStyle } from '../../components/ui/StatusChip/StatusChip.types';
import {
  INV_AVAILABILITY_IN_REQUEST_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_TRANSIT_LABEL,
} from '../../constants/inventory-status-labels';
import {
  SaleFulfillmentGroupStatus,
  SaleFulfillmentSummary,
  SalesOrderFulfillmentStatus,
} from '../../types/sales.types';

/**
 * Single source of truth for how mixed-branch (SOL/TRL) logistics is presented
 * across the sales registry: order-level chips, group-level chips and the
 * step/timeline visualization. Keeping labels/colors here avoids the previous
 * drift between `statusMap.ts` and `salesStatus.ts`.
 */

export type FulfillmentEmphasis = 'action' | 'passive';

export interface OrderFulfillmentPresentation {
  /** Short chip label. */
  label: string;
  /** Longer explanation for tooltips / detail header. */
  helperText: string;
  statusType: StatusType;
  visualStyle: StatusVisualStyle;
  withIcon: boolean;
  /** `action` highlights that the user must do something (finalize the sale). */
  emphasis: FulfillmentEmphasis;
}

const ORDER_FULFILLMENT_PRESENTATION: Record<
  Exclude<SalesOrderFulfillmentStatus, 'NOT_REQUIRED'>,
  OrderFulfillmentPresentation
> = {
  PENDING: {
    label: 'Envío pendiente',
    helperText: 'Las piezas remotas aún están en camino a la sucursal de venta.',
    statusType: 'warning',
    visualStyle: 'outline',
    withIcon: false,
    emphasis: 'passive',
  },
  COMPLETED: {
    label: 'Envío completado',
    helperText: 'Todas las piezas remotas llegaron a la sucursal de venta.',
    statusType: 'success',
    visualStyle: 'outline',
    withIcon: false,
    emphasis: 'passive',
  },
  FAILED: {
    label: 'Rechazada',
    helperText: 'Al menos un traslado fue rechazado o cancelado. Revisa el detalle.',
    statusType: 'error',
    visualStyle: 'outline',
    withIcon: true,
    emphasis: 'passive',
  },
  CANCELLED: {
    label: 'Envío cancelado',
    helperText: 'El envío multi-sucursal se anuló por devolución o cancelación de la venta.',
    statusType: 'error',
    visualStyle: 'outline',
    withIcon: true,
    emphasis: 'passive',
  },
};

/** Returns null for NOT_REQUIRED / unknown (caller should render no chip). */
export function getOrderFulfillmentPresentation(
  status: string | null | undefined,
): OrderFulfillmentPresentation | null {
  if (!status || status === 'NOT_REQUIRED') return null;
  // Legacy pre-migration rows — present as completed until DB is clean.
  if (status === 'READY') {
    return ORDER_FULFILLMENT_PRESENTATION.COMPLETED;
  }
  return (
    ORDER_FULFILLMENT_PRESENTATION[
      status as Exclude<SalesOrderFulfillmentStatus, 'NOT_REQUIRED'>
    ] ?? null
  );
}

/**
 * Micro-indicator of progress for the list view, e.g. "1/3 envíos recibidos".
 * Returns null when there is nothing meaningful to show.
 */
export function getFulfillmentProgressCaption(
  status: string | null | undefined,
  summary: SaleFulfillmentSummary | null | undefined,
): string | null {
  if (!summary || summary.total <= 0) return null;

  switch (status) {
    case 'PENDING':
      return `${summary.received}/${summary.total} envíos recibidos`;
    case 'FAILED':
      return summary.failed > 0 ? `${summary.failed}/${summary.total} envíos con problema` : null;
    case 'CANCELLED':
    case 'COMPLETED':
    case 'READY':
      return null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Group-level presentation (per remote source location)
// ---------------------------------------------------------------------------

export interface GroupStatusPresentation {
  label: string;
  statusType: StatusType;
}

const GROUP_STATUS_PRESENTATION: Record<SaleFulfillmentGroupStatus, GroupStatusPresentation> = {
  PENDING_SOL: { label: INV_AVAILABILITY_IN_REQUEST_LABEL, statusType: 'warning' },
  AWAITING_SOURCE: { label: INV_AVAILABILITY_IN_REQUEST_LABEL, statusType: 'warning' },
  IN_TRANSIT: { label: INV_STATUS_TRANSIT_LABEL, statusType: 'info' },
  RECEIVED: { label: INV_STATUS_BRANCH_LABEL, statusType: 'success' },
  FAILED: { label: 'Rechazada', statusType: 'error' },
};

export function getGroupStatusPresentation(
  status: SaleFulfillmentGroupStatus,
): GroupStatusPresentation {
  return GROUP_STATUS_PRESENTATION[status] ?? { label: status, statusType: 'default' };
}

/**
 * Operational logistics stage (SOL / TRL facts over a possibly stale
 * stored group.status). Keep in sync with backend
 * `resolve-logistics-stage.util.ts`.
 */
export interface ResolveLogisticsStageInput {
  storedStatus: SaleFulfillmentGroupStatus;
  requestStatus?: string | null;
  transferStatus?: string | null;
  /** True when remote piece(s) are already at the selling branch (WIP). */
  pieceAtSellingBranch?: boolean;
}

export function resolveLogisticsStage(
  input: ResolveLogisticsStageInput,
): SaleFulfillmentGroupStatus {
  const { storedStatus, requestStatus, transferStatus, pieceAtSellingBranch } = input;

  if (storedStatus === 'FAILED' || requestStatus === 'REJECTED') {
    return 'FAILED';
  }

  if (
    storedStatus === 'RECEIVED' ||
    transferStatus === 'COMPLETED' ||
    pieceAtSellingBranch === true
  ) {
    return 'RECEIVED';
  }

  // Active TRL ⇒ in transit (same truth the pieces/inventory table reflects).
  if (transferStatus === 'PENDING' || transferStatus === 'IN_TRANSIT') {
    return 'IN_TRANSIT';
  }

  // SOL approved creates a TRL in the same resolve; treat as in transit even
  // if the group FK/status sync was skipped.
  if (requestStatus === 'APPROVED') {
    return 'IN_TRANSIT';
  }

  if (storedStatus === 'IN_TRANSIT') {
    return 'IN_TRANSIT';
  }

  if (storedStatus === 'AWAITING_SOURCE') {
    return 'AWAITING_SOURCE';
  }

  return 'PENDING_SOL';
}

// ---------------------------------------------------------------------------
// Timeline / stepper model
// ---------------------------------------------------------------------------

export interface FulfillmentTimelineStep {
  key: 'request' | 'approval' | 'transit' | 'received';
  label: string;
}

/** Ordered logistics stages a group traverses from SOL to reception. */
export const FULFILLMENT_TIMELINE_STEPS: FulfillmentTimelineStep[] = [
  { key: 'request', label: 'Solicitud' },
  { key: 'approval', label: 'Aprobación' },
  { key: 'transit', label: INV_STATUS_TRANSIT_LABEL },
  { key: 'received', label: 'Recibida' },
];

export type TimelineNodeState = 'done' | 'active' | 'pending' | 'failed';

export interface GroupTimeline {
  label: string;
  statusType: StatusType;
  failed: boolean;
  /** One entry per FULFILLMENT_TIMELINE_STEPS item. */
  nodes: TimelineNodeState[];
}

export interface GroupTimelineOpts {
  hasRequest?: boolean;
  hasTransfer?: boolean;
  /** When set, stage is derived from SOL/TRL (SSOT) instead of raw status. */
  requestStatus?: string | null;
  transferStatus?: string | null;
  pieceAtSellingBranch?: boolean;
}

/**
 * Maps logistics stage to a per-step timeline. Prefer passing request/transfer
 * statuses so a stale PENDING_SOL cannot disagree with the pieces table.
 */
export function getGroupTimeline(
  status: SaleFulfillmentGroupStatus,
  opts: GroupTimelineOpts = {},
): GroupTimeline {
  const effectiveStatus = resolveLogisticsStage({
    storedStatus: status,
    requestStatus: opts.requestStatus,
    transferStatus: opts.transferStatus,
    pieceAtSellingBranch: opts.pieceAtSellingBranch,
  });
  const presentation = getGroupStatusPresentation(effectiveStatus);
  const stepCount = FULFILLMENT_TIMELINE_STEPS.length;

  if (effectiveStatus === 'FAILED') {
    // Failure lands on the furthest stage actually reached.
    const failedIndex = opts.hasTransfer ? 2 : opts.hasRequest ? 1 : 0;
    const nodes: TimelineNodeState[] = FULFILLMENT_TIMELINE_STEPS.map((_, i) => {
      if (i < failedIndex) return 'done';
      if (i === failedIndex) return 'failed';
      return 'pending';
    });
    return { label: presentation.label, statusType: presentation.statusType, failed: true, nodes };
  }

  if (effectiveStatus === 'RECEIVED') {
    return {
      label: presentation.label,
      statusType: presentation.statusType,
      failed: false,
      nodes: FULFILLMENT_TIMELINE_STEPS.map(() => 'done'),
    };
  }

  const activeIndex =
    effectiveStatus === 'PENDING_SOL'
      ? 0
      : effectiveStatus === 'AWAITING_SOURCE'
        ? 1
        : /* IN_TRANSIT */ 2;

  const nodes: TimelineNodeState[] = Array.from({ length: stepCount }, (_, i) => {
    if (i < activeIndex) return 'done';
    if (i === activeIndex) return 'active';
    return 'pending';
  });

  return { label: presentation.label, statusType: presentation.statusType, failed: false, nodes };
}
